// @flow
import { ErrorMessage } from 'formik';
import * as React from 'react';
import { Box, Text, CheckBox as GrommetCheckbox } from 'grommet';
import { withTheme } from 'styled-components';
import * as Yup from 'yup';
import { defineMessages } from 'react-intl';
import { memoize } from 'lodash';
import RequiredAsterisk from '../RequiredAsterisk';


type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        setFieldValue: (string, any) => void,
        isSubmitting: boolean,
    },
    required: boolean,
    label: string,
    help_text: string,
    theme: Object,
    choices: Object,
}


class MultipleCheckboxes extends React.PureComponent<Props> {
    constructor(props: Props) {
        super(props);
        this.getValue = memoize(this.getValue);
    }

    getValue = (value: Array<mixed> | null): any => {
        if (!value) return [];

        return value.map((v) => v.toString());
    };

    handleChange = (e) => {
        const { form: { setFieldValue }, field: { name, value } } = this.props;
        const newValue = this.getValue(value);
        const index = newValue.indexOf(e.target.value);
        if (e.target.checked && index === -1) {
            newValue.push(e.target.value);
        } else if (index > -1) {
            newValue.splice(index, 1);
        }
        setFieldValue(name, newValue);
    };

    getLabel = (choiceLabel) => {
        const splitText = choiceLabel ? choiceLabel.split('\n') : [];
        return (
            <Text>
                {
                    splitText.map((text, index) => (
                        <>
                            {text}
                            {index < splitText.length - 1 ? <br /> : null}
                        </>
                    ))
                }
            </Text>
        );
    };

    render() {
        const {
            field: {
                name, value,
            },
            form: { isSubmitting },
            label,
            help_text: helpText,
            required,
            theme,
            choices,
        } = this.props;

        const { formField: { error: errorStyle, label: labelStyle, help: helpStyle } } = theme;

        const valueMapped = this.getValue(value);

        return (
            <Box flex={false}>
                {label && (
                    <Text as="label" htmlFor={`id-${name}`} {...labelStyle}>
                        {label}
                        {required ? <RequiredAsterisk /> : null}
                    </Text>
                )}
                <Box direction="column" wrap>
                    {choices && Object.keys(choices).map((key) => (
                        <Box key={`check-${key}`} wrap pad={{ vertical: 'small' }}>
                            <GrommetCheckbox
                                name={name}
                                checked={valueMapped.indexOf(key) > -1}
                                onChange={this.handleChange}
                                value={key}
                                label={this.getLabel(choices[key])}
                                disabled={isSubmitting}
                            />
                            {helpText && (
                                <Text {...helpStyle}>{helpText}</Text>
                            )}
                            <ErrorMessage name={name}>
                                {(msg) => <Text {...errorStyle}>{msg}</Text>}
                            </ErrorMessage>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }
}

const messages = defineMessages({
    required: {
        id: 'checkbox-required',
        defaultMessage: 'This field is required.',
    },
});

MultipleCheckboxes.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required(messages.required).nullable();
    }

    return Yup.string().nullable();
};

export default withTheme(MultipleCheckboxes);
